import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { objectsApi, type AnomalyHistoryPoint } from '../api'
import { ShieldAlert, ClockAlert, Ruler, FileDiff, Diff, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'

interface AnomalyHistoryChartProps {
    objectID: string
}

type MetricKey = 'size' | 'fileCount' | 'transferTime'

function formatSize(mb: number | null | undefined): string {
    if (mb == null) return '-'
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`
    if (mb >= 1) return `${mb.toFixed(1)} MB`
    return `${(mb * 1024).toFixed(0)} KB`
}

const METRIC_CONFIG: Record<MetricKey, { label: string; color: string; field: keyof AnomalyHistoryPoint }> = {
    size: { label: 'Size', color: '#3b82f6', field: 'sizeMB' },
    fileCount: { label: 'File Count', color: '#22c55e', field: 'noVaultFiles' },
    transferTime: { label: 'Transfer Time (s)', color: '#a855f7', field: 'transferTime' },
}

const CATEGORY_ICONS: Record<string, typeof ClockAlert> = {
    timing: ClockAlert, cadence: ClockAlert,
    size: Ruler, sizeDelta: Ruler,
    files: FileDiff, fileCount: FileDiff, fileMismatch: FileDiff,
    shape: Diff, structural: Diff, vaultId: Diff, primary: Diff, secondary: Diff,
}

function getScoreColor(score: number): string {
    if (score >= 7) return '#dc2626'
    if (score >= 4) return '#ea580c'
    if (score >= 1) return '#f59e0b'
    return '#22c55e'
}

function getScoreBgClass(score: number): string {
    if (score >= 7) return 'border-red-500 bg-red-500/20 text-red-700'
    if (score >= 4) return 'border-orange-500 bg-orange-500/20 text-orange-700'
    if (score >= 1) return 'border-amber-500 bg-amber-500/20 text-amber-700'
    return 'border-green-500 bg-green-500/20 text-green-700'
}

function parseAnomalyDetails(anomalyData: any): Array<{ type: string; description: string; category: string; severity: number }> {
    if (!anomalyData) return []
    let parsed = anomalyData
    if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed) } catch { return [] }
    }
    if (!parsed || typeof parsed !== 'object') return []

    const results: Array<{ type: string; description: string; category: string; severity: number }> = []

    // Format 1: { checks_run: N, anomalies: [...] }
    if (Array.isArray(parsed.anomalies)) {
        for (const a of parsed.anomalies) {
            const type = a?.type || 'unknown'
            const cat = ['timing', 'cadence'].includes(type) ? 'timing'
                : ['size', 'sizeDelta'].includes(type) ? 'size'
                : ['fileCount', 'fileMismatch'].includes(type) ? 'files'
                : 'shape'
            results.push({ type, description: a?.description || type, category: cat, severity: 1 })
        }
        return results
    }

    // Format 2: { category: { severity: N, types: [...] } }
    for (const [cat, val] of Object.entries(parsed)) {
        if (cat === 'checks_run') continue
        if (val && typeof val === 'object' && 'types' in (val as any)) {
            const sev = (val as any).severity || 1
            for (const t of (val as any).types || []) {
                results.push({ type: t?.type || cat, description: t?.description || '', category: cat, severity: sev })
            }
        }
    }
    return results
}

function AnomalyIconStrip({ anomalyData, score }: { anomalyData: any; score: number }) {
    const details = parseAnomalyDetails(anomalyData)
    if (details.length === 0 && score <= 0) return null

    const categorySeverity = new Map<string, number>()
    for (const d of details) {
        const current = categorySeverity.get(d.category) || 0
        if (d.severity > current) categorySeverity.set(d.category, d.severity)
    }

    return (
        <span className="inline-flex items-center gap-1">
            {Array.from(categorySeverity.entries()).map(([cat, sev]) => {
                const IconComp = CATEGORY_ICONS[cat] || AlertTriangle
                return (
                    <span key={cat} className="relative">
                        <IconComp
                            className="h-4 w-4"
                            strokeWidth={sev >= 2 ? 2.2 : 1.5}
                            style={{ opacity: sev >= 2 ? 1 : 0.5, color: sev >= 2 ? '#dc2626' : '#f59e0b' }}
                        />
                        {sev >= 2 && (
                            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500" />
                        )}
                    </span>
                )
            })}
        </span>
    )
}

export function AnomalyHistoryChart({ objectID }: AnomalyHistoryChartProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [chartType, setChartType] = useState<'line' | 'bar'>('line')
    const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(new Set(['size']))

    const { data: rawData = [], isLoading } = useQuery({
        queryKey: ['objects', objectID, 'anomaly-history'],
        queryFn: async () => {
            const response = await objectsApi.getAnomalyHistory(objectID)
            const bd = response.data as any
            return (bd?.data || bd || []) as AnomalyHistoryPoint[]
        },
        enabled: !!objectID && open,
    })

    const anomalyCount = rawData.filter(d => d.anomalyScore > 0).length
    const maxScore = Math.max(0, ...rawData.map(d => d.anomalyScore || 0))

    const toggleMetric = (key: MetricKey) => {
        setActiveMetrics((prev) => {
            const next = new Set(prev)
            if (next.has(key)) { if (next.size > 1) next.delete(key) } else { next.add(key) }
            return next
        })
    }

    const chartOption = useMemo((): EChartsOption => {
        if (rawData.length === 0) return {}

        const dates = rawData.map((d) => {
            if (!d.datetime) return ''
            const dt = new Date(d.datetime)
            return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })
        })

        const activeKeys = Array.from(activeMetrics)
        const useDualAxis = activeKeys.length > 1
        const leftKey = activeKeys.includes('size') ? 'size' : activeKeys[0]
        const rightKeys = activeKeys.filter((k) => k !== leftKey)

        const series: any[] = []
        const isBar = chartType === 'bar'

        for (const key of activeKeys) {
            const config = METRIC_CONFIG[key]
            const values = rawData.map((d) => {
                const v = d[config.field as keyof AnomalyHistoryPoint]
                return v != null ? Number(v) : null
            })
            const isRight = useDualAxis && rightKeys.includes(key)

            const baseSeries: any = {
                name: config.label,
                type: isBar ? 'bar' : 'line',
                data: values,
                yAxisIndex: isRight ? 1 : 0,
                z: 2,
            }

            if (isBar) {
                baseSeries.barMaxWidth = 20
                baseSeries.itemStyle = {
                    color: (params: any) => {
                        const point = rawData[params.dataIndex]
                        if (point?.anomalyScore > 0) return getScoreColor(point.anomalyScore)
                        return config.color
                    },
                    borderRadius: [2, 2, 0, 0],
                }
            } else {
                baseSeries.lineStyle = { width: 2, color: config.color }
                baseSeries.areaStyle = { color: `${config.color}10` }
                baseSeries.symbol = 'circle'
                baseSeries.symbolSize = 3
                baseSeries.itemStyle = { color: config.color }
            }

            // Anomaly markPoints on top of the series — large highlighted markers
            const markPointData: any[] = []
            rawData.forEach((d, i) => {
                if (d.anomalyScore > 0) {
                    markPointData.push({
                        coord: [i, values[i] ?? 0],
                        symbol: 'pin',
                        symbolSize: d.anomalyScore >= 4 ? 40 : 30,
                        itemStyle: {
                            color: getScoreColor(d.anomalyScore),
                            borderColor: '#fff',
                            borderWidth: 2,
                            shadowColor: getScoreColor(d.anomalyScore),
                            shadowBlur: 8,
                        },
                        label: {
                            show: true,
                            formatter: `${d.anomalyScore}`,
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 'bold',
                            offset: [0, -2],
                        },
                    })
                }
            })

            if (markPointData.length > 0) {
                baseSeries.markPoint = {
                    data: markPointData,
                    animation: true,
                }
            }

            series.push(baseSeries)
        }

        // Anomaly markArea bands — highlight anomaly regions with faint red background
        const markAreaData: any[] = []
        rawData.forEach((d, i) => {
            if (d.anomalyScore > 0) {
                markAreaData.push([
                    { xAxis: Math.max(0, i - 0.5), itemStyle: { color: d.anomalyScore >= 4 ? 'rgba(220,38,38,0.1)' : 'rgba(245,158,11,0.08)' } },
                    { xAxis: Math.min(rawData.length - 1, i + 0.5) },
                ])
            }
        })

        if (series.length > 0 && markAreaData.length > 0) {
            series[0].markArea = { silent: true, data: markAreaData }
        }

        // Determine axis label — auto-detect GB vs MB for size
        const leftLabel = (() => {
            if (leftKey !== 'size') return METRIC_CONFIG[leftKey].label
            const maxVal = Math.max(0, ...rawData.map(d => Number(d.sizeMB ?? 0)))
            return maxVal >= 1024 ? 'Size (GB)' : 'Size (MB)'
        })()
        const sizeInGB = leftKey === 'size' && Math.max(0, ...rawData.map(d => Number(d.sizeMB ?? 0))) >= 1024

        const yAxis: any[] = [{
            type: 'value',
            name: leftLabel,
            nameTextStyle: { fontSize: 10, color: '#888' },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
            axisLabel: {
                fontSize: 10, color: '#888',
                formatter: sizeInGB ? (v: number) => (v / 1024).toFixed(1) : undefined,
            },
        }]

        if (useDualAxis && rightKeys.length > 0) {
            yAxis.push({
                type: 'value',
                name: METRIC_CONFIG[rightKeys[0]].label,
                nameTextStyle: { fontSize: 10, color: '#888' },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: { fontSize: 10, color: '#888' },
            })
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', label: { show: false } },
                formatter: (params: any) => {
                    if (!Array.isArray(params) || params.length === 0) return ''
                    const idx = params[0].dataIndex
                    const point = rawData[idx]
                    if (!point) return ''

                    let html = `<div style="font-weight:600;margin-bottom:4px">${dates[idx]}</div>`
                    if (point.objectSecondary) html += `<div style="color:#888;font-size:11px;margin-bottom:4px">${point.objectSecondary} backup</div>`

                    for (const key of activeKeys) {
                        const config = METRIC_CONFIG[key]
                        const v = point[config.field as keyof AnomalyHistoryPoint]
                        let display: string
                        if (key === 'size') {
                            display = formatSize(v != null ? Number(v) : null)
                        } else {
                            display = v != null ? String(Number(v).toFixed(0)) : '-'
                            if (key === 'transferTime' && v != null) display += 's'
                        }
                        html += `<div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${config.color};margin-right:6px"></span>${config.label}: <b>${display}</b></div>`
                    }

                    if (point.anomalyScore > 0) {
                        const scoreColor = getScoreColor(point.anomalyScore)
                        html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb">`
                        html += `<div style="color:${scoreColor};font-weight:700;font-size:13px">⚠ Anomaly Score: ${point.anomalyScore}/8</div>`

                        const details = parseAnomalyDetails(point.anomalyData)
                        if (details.length > 0) {
                            html += `<div style="margin-top:4px">`
                            for (const d of details) {
                                const iconLabel = d.category === 'timing' ? '⏱' : d.category === 'size' ? '📏' : d.category === 'files' ? '📁' : '🔀'
                                const sevLabel = d.severity >= 2 ? '🔴' : '🟡'
                                html += `<div style="font-size:11px;margin-top:2px">${iconLabel} ${sevLabel} <b>${d.type}</b>: ${d.description}</div>`
                            }
                            html += `</div>`
                        }
                        html += `</div>`
                    }

                    return html
                },
            },
            legend: { show: false },
            grid: { left: 15, right: useDualAxis ? 15 : 15, top: 25, bottom: 30, containLabel: true },
            xAxis: {
                type: 'category',
                data: dates,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { fontSize: 9, color: '#888', rotate: 35 },
            },
            yAxis,
            series,
        }
    }, [rawData, activeMetrics, chartType])

    // Anomaly summary table for the dialog
    const anomalyVersions = rawData.filter(d => d.anomalyScore > 0)

    return (
        <>
            {/* Button to open */}
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
                <ShieldAlert className="h-4 w-4" />
                {t('objects.anomalyHistory', 'Anomaly History')}
                {anomalyCount > 0 && (
                    <Badge variant="outline" className={`ml-1 text-[10px] px-1.5 py-0 ${getScoreBgClass(maxScore)}`}>
                        {anomalyCount}
                    </Badge>
                )}
            </Button>

            {/* Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5" />
                            {t('objects.anomalyHistory', 'Anomaly History')} — {objectID}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Controls row */}
                    <div className="flex items-center gap-3 mb-2">
                        {/* Chart type toggle */}
                        <div className="flex items-center gap-0.5 p-0.5 rounded-lg border bg-muted/30">
                            <button
                                type="button"
                                onClick={() => setChartType('line')}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    chartType === 'line' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                Line
                            </button>
                            <button
                                type="button"
                                onClick={() => setChartType('bar')}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    chartType === 'bar' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                Bar
                            </button>
                        </div>

                        {/* Metric toggle pills */}
                        {(Object.keys(METRIC_CONFIG) as MetricKey[]).map((key) => {
                            const config = METRIC_CONFIG[key]
                            const isActive = activeMetrics.has(key)
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => toggleMetric(key)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                        isActive ? 'border-transparent text-white' : 'border-border text-muted-foreground hover:text-foreground'
                                    }`}
                                    style={isActive ? { backgroundColor: config.color } : undefined}
                                >
                                    {config.label}
                                </button>
                            )
                        })}

                        {anomalyCount > 0 && (
                            <span className="ml-auto text-xs text-muted-foreground">
                                {anomalyCount} anomalies across {rawData.length} versions
                            </span>
                        )}
                    </div>

                    {/* Chart */}
                    {isLoading ? (
                        <div className="flex items-center justify-center h-[400px] text-sm text-muted-foreground">Loading...</div>
                    ) : rawData.length === 0 ? (
                        <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">No version data available</div>
                    ) : (
                        <ReactECharts key={`${chartType}-${Array.from(activeMetrics).join(',')}`} option={chartOption} notMerge={true} style={{ height: 400 }} opts={{ renderer: 'canvas' }} />
                    )}

                    {/* Anomaly details table */}
                    {anomalyVersions.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                Flagged Versions ({anomalyVersions.length})
                            </h4>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-muted/50 border-b">
                                            <th className="text-left px-3 py-2 font-medium">Date</th>
                                            <th className="text-left px-3 py-2 font-medium">Version</th>
                                            <th className="text-left px-3 py-2 font-medium">Type</th>
                                            <th className="text-center px-3 py-2 font-medium">Score</th>
                                            <th className="text-left px-3 py-2 font-medium">Categories</th>
                                            <th className="text-left px-3 py-2 font-medium">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {anomalyVersions.map((v) => {
                                            const details = parseAnomalyDetails(v.anomalyData)
                                            return (
                                                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        {v.datetime ? new Date(v.datetime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 font-mono truncate max-w-[180px]" title={v.objectName}>{v.objectName || '-'}</td>
                                                    <td className="px-3 py-2">{v.objectSecondary || '-'}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        <Badge variant="outline" className={`text-[10px] ${getScoreBgClass(v.anomalyScore)}`}>
                                                            {v.anomalyScore}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <AnomalyIconStrip anomalyData={v.anomalyData} score={v.anomalyScore} />
                                                    </td>
                                                    <td className="px-3 py-2 text-muted-foreground max-w-[300px] truncate">
                                                        {details.map(d => d.description).join('; ') || '-'}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
